import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

@Directive({
  selector: '[slcPopover]',
  standalone: true,
})
export class SlicePopoverDirective {
  // Inputs
  template = input<TemplateRef<any> | null>(null, { alias: 'slcPopover' });
  context = input<any>({}, { alias: 'slcPopoverContext' });
  overlayClass = input<string | string[] | null>(null, {
    alias: 'slcPopoverOverlayClass',
  });

  // Injectables
  private el = inject(ElementRef);
  private overlay = inject(Overlay);
  private renderer = inject(Renderer2);
  private viewContainer = inject(ViewContainerRef);

  // Private
  private arrow: any | null = null;
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;

  @HostListener('mouseenter', ['$event']) onMouseEnter(event: MouseEvent) {
    this.createPopover();
  }

  @HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent) {
    this.destroyPopover();
  }

  private createOverlay() {
    if (!this.template()) {
      return;
    }

    const overlayClass = this.overlayClass();

    // Cria o arrow
    const arrow = this.renderer.createElement('div');
    this.renderer.addClass(arrow, 'slc-popover-arrow');
    this.arrow = arrow;

    // Aplica as mesmas classes do overlay na arrow
    if (overlayClass) {
      if (Array.isArray(overlayClass)) {
        overlayClass.forEach((cl) => this.renderer.addClass(arrow, cl));
      } else {
        this.renderer.addClass(arrow, overlayClass);
      }
    }

    // Inclui temporariamente no DOM para obter a altura original
    this.renderer.appendChild(document.body, arrow);
    const arrowRect = arrow?.getBoundingClientRect() as DOMRect;
    const arrowSize = arrowRect.height || 0;
    this.renderer.removeChild(document.body, this.arrow);

    this.portal = new TemplatePortal(this.template()!, this.viewContainer, this.context());

    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: arrowSize,
        offsetX: -arrowSize,
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -arrowSize,
        offsetX: -arrowSize,
      },
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: -arrowSize,
        offsetX: arrowSize,
      },
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: arrowSize,
        offsetX: arrowSize,
      },
    ];

    const panelClass = overlayClass
      ? Array.isArray(overlayClass)
        ? ['slc-popover-container', ...overlayClass]
        : ['slc-popover-container', overlayClass]
      : 'slc-popover-container';
    const config = new OverlayConfig({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.el)
        .withPositions(positions)
        .withLockedPosition()
        .withGrowAfterOpen(),
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass,
    });

    this.overlayRef = this.overlay.create(config);
  }

  private createPopover() {
    this.createOverlay();

    if (!this.overlayRef?.hasAttached()) {
      this.portal!.context = this.context();
      this.overlayRef?.attach(this.portal);
      this.showArrow();
    }
  }

  private destroyPopover() {
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.portal = null;
    this.renderer.removeChild(document.body, this.arrow);
    this.arrow = null;
  }

  private showArrow() {
    setTimeout(() => {
      // Posiciona o arrow
      const arrow = this.arrow;
      this.renderer.appendChild(document.body, arrow);
      const arrowRect = this.arrow.getBoundingClientRect() as DOMRect;
      const hostRect = this.el.nativeElement.getBoundingClientRect();
      const overlayRect = this.overlayRef!.overlayElement.getBoundingClientRect();

      this.renderer.removeClass(arrow, 'slc-popover-arrow-top');
      this.renderer.removeClass(arrow, 'slc-popover-arrow-bottom');

      const left = hostRect.left + hostRect.width / 2 + window.scrollX;
      let top = 0;
      if (hostRect.top > overlayRect.top) {
        top = hostRect.top - arrowRect.height - 2 + window.scrollY;
        this.renderer.addClass(arrow, 'slc-popover-arrow-top');
      } else {
        top = hostRect.bottom + 2 + window.scrollY;
        this.renderer.addClass(arrow, 'slc-popover-arrow-bottom');
      }

      this.renderer.setStyle(arrow, 'top', `${top}px`);
      this.renderer.setStyle(arrow, 'left', `${left}px`);
    });
  }
}
